// @flow

import React from 'react';
import useSheet from 'app/jss';
import classNames from 'classnames';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { get, debounce, sortBy, some, compact } from 'lodash';
import t from 'app/utils/translate';
import { FRIEND_GROUP_ID, NEW_GROUP_ID } from 'app/constants/friendConstants';
import { createGroup, deleteGroup } from 'app/actions/groupActions';
import { searchUsers, clearResults } from 'app/actions/friendSearchActions';
import { viewFriend } from 'app/actions/friendsActions';
import * as Enter from 'app/utils/animations/enter';
import * as Leave from 'app/utils/animations/leave';
import manageFriendsSelector from 'app/selectors/friends/manageFriendsSelector';
import GroupContainer from 'app/components/friends/GroupContainer';
import FriendInfoMessage from 'app/components/friends/common/FriendInfoMessage';
import FriendInfoMessageItem from 'app/components/friends/common/FriendInfoMessageItem';
import Icon from 'app/components/commons/Icon';
import GroupEditableTitle from 'app/components/friends/groups/GroupEditableTitle';
import GroupContainerHeader from 'app/components/friends/GroupContainerHeader';
import GroupContainerSearch from 'app/components/friends/GroupContainerSearch';
import FriendsRowList from 'app/components/friends/FriendsRowList';
import Tappable from 'app/components/commons/Tappable';
import GroupContentsSearchResults from 'app/components/friends/GroupContentsSearchResults';
import FriendSlider from 'app/components/friends/common/FriendSlider';
import FriendRow from 'app/components/friends/FriendRow';
import FriendLoader from 'app/components/friends/common/FriendsLoader';
import AnimatedGroup from 'app/components/commons/AnimatedGroup';
import AnimatedGroupChild from 'app/components/commons/AnimatedGroupChild';

type Props = {
  classes: Object,
  className?: string,
  isSaving: boolean,
  manageableFriends?: Array<iFriendUser>,
  allRequests: Object,
  onClearSearchResults: (searchId: number | string) => void,
  onSearchUsers: (term: string) => void,
  onCreateGroup: (title: string) => void,
  onRemoveGroup: (groupId: string | number) => void,
  onViewFriend: (id: string | number) => void,
  anyFriends: boolean,
  history: Object,
  groups: Object,
  isMobile?: boolean,
};

type State = {
  selectedUser: iFriendUser,
  searchTerm: string,
  isSearching: boolean,
  title: string,
  members: Set<number | string>,
  invalidTitle: boolean,
  editTitle: boolean,
};

class GroupContentsNew extends React.Component {
  props: Props;
  state: State = {
    selectedUser: undefined,
    searchTerm: '',
    title: '',
    isSearching: false,
    members: new Set(),
    editTitle: true,
  };

  static defaultProps = {
    manageableFriends: [],
    searchResults: [],
    isSearching: false,
    className: undefined,
    isMobile: true,
  };

  saveGroup = () => {
    const { title } = this.state;
    if (title) {
      this.props.onCreateGroup({
        id: NEW_GROUP_ID,
        members: [...this.state.members],
        name: title,
      });
    }
  }

  cancelNew = () => {
    this.props.history.push(`/groups/${FRIEND_GROUP_ID}`);
  }

  onAddToGroup = (groupId: number, userId: string | number) => {
    this.state.members.add(userId);
    this.setState({ members: this.state.members });
  }

  onRemoveFromGroup = (groupId: number, userId: string | number) => {
    this.state.members.delete(userId);
    this.setState({ members: this.state.members });
  }

  onCreateFriendRequest = (friendIds: Array<number | string>) => {
    this.onAddToGroup(NEW_GROUP_ID, friendIds[0]);
  }

  onMessageClick = (item) => {
    if (item.id === 1) {
      this.setState({ isSearching: true });
    }
  }

  toggleUser = (user) => {
    const {
      onViewFriend,
    } = this.props;

    onViewFriend(user);
    if (this.state.selectedUser && this.state.selectedUser.id === user.id) {
      this.setState({ selectedUser: undefined });
    } else {
      this.setState({ selectedUser: user });
    }
  }

  triggerSearch = debounce((term) => {
    this.props.onSearchUsers(term, NEW_GROUP_ID);
    this.setState({ searchTerm: term });
  }, this.props.isMobile ? 700 : 400)

  onSearchChange = (terms: string) => {
    if (terms) {
      this.triggerSearch(terms);
    } else {
      // if they cleared it, set it straitht away
      this.setState({ searchTerm: terms });
      this.props.onClearSearchResults(NEW_GROUP_ID);
    }
  }

  onSearchStart = () => {
    this.setState({ isSearching: true });
  }

  onSearchEnd = () => {
    this.setState({ isSearching: false });
  }

  onTitleChange = (title: string, isInvalid) => {
    this.setState({ title, invalidTitle: isInvalid });
  }

  render() {
    const {
      classes,
      manageableFriends,
      allRequests,
      isSaving,
      className,
      anyFriends,
      groups,
    } = this.props;
    const {
      searchTerm,
      title,
      selectedUser,
      isSearching,
      members,
      invalidTitle,
      editTitle,
    } = this.state;

    const memberArray = [...members.values()];
    const managableUsers = [...manageableFriends, ...compact(memberArray.map(id => allRequests[id]))];


    const saveDisabled = !title || invalidTitle;
    return (
      <div className={classNames(classes.groupContents, className)}>
        <FriendSlider
          friend={selectedUser}
          onBack={() => this.toggleUser(selectedUser)}
        />
        <GroupContainer className={classes.rounded}>
          <GroupContainerHeader>
            <GroupEditableTitle
              title={title}
              onTitleChange={this.onTitleChange}
              onTitleSave={() => this.setState({ editTitle: false })}
              rightIcon="icon-cross"
              onShrink={this.cancelNew}
              editMode={editTitle}
              invalidConfig={[{
                id: 'groupexists',
                isInvalid: newTitle => some(groups, g => g.name === newTitle),
                reason: t('Group name already exists'),
              }]}
            />
            <AnimatedGroup className={classes.searchRow}>
              <GroupContainerSearch
                className={classes.searchBar}
                isSearching={isSearching}
                onSearchChange={this.onSearchChange}
                onSearchStart={this.onSearchStart}
                onSearchEnd={this.onSearchEnd}
              />
              {!isSearching &&
                <AnimatedGroupChild
                  onEnter={Enter.slideLeftAndGrow(0.3, 0.3)}
                  onLeave={Leave.slideRightAndShrink(0.3)}
                >
                  <Tappable
                    className={classNames(classes.doneButton, { disabled: saveDisabled })}
                    onTap={saveDisabled ? undefined : this.saveGroup}
                  >
                    <Icon type="icon-tick" size={9} />
                    <span>{t('Save')}</span>
                  </Tappable>
                </AnimatedGroupChild>
              }
            </AnimatedGroup>
          </GroupContainerHeader>
          <div className={classNames(classes.innerContainer, { isSearching: !!searchTerm })}>
            {isSaving &&
              <FriendLoader showOverlay>
                {`${t('Creating *title*').replace('*title*', title)}...`}
              </FriendLoader>
            }
            {!anyFriends && !searchTerm &&
              <FriendInfoMessage>
                <FriendInfoMessageItem
                  icon="icon-group"
                  text={t('name your group')}
                  color="primary"
                  onClick={() => this.setState({ editTitle: true })}
                />
                <FriendInfoMessageItem
                  icon="icon-search"
                  text={t('find friends from search')}
                  color="primary"
                  onClick={() => this.setState({ isSearching: true })}
                />
              </FriendInfoMessage>}
          </div>
          {searchTerm && (
            <GroupContentsSearchResults
              members={memberArray}
              onAddToGroup={this.onAddToGroup}
              onCreateFriendRequest={this.onCreateFriendRequest}
              searchId={NEW_GROUP_ID}
              searchTerm={searchTerm}
              isNew
            />
          )}
          {!searchTerm && (
            <FriendsRowList>
              {sortBy(managableUsers, 'username').map(user => (
                <FriendRow
                  key={user.id}
                  friend={user}
                  onRowClick={() => this.toggleUser(user)}
                  onAddToGroup={this.onAddToGroup}
                  onRemoveFriend={this.onRemoveFromGroup}
                  onCreateFriendRequest={this.onCreateFriendRequest}
                  members={memberArray}
                />
              ))}
            </FriendsRowList>
          )}
        </GroupContainer>
      </div>
    );
  }
}

function applyStyles(theme: Theme) {
  return {
    groupContents: {
      position: 'relative',
      display: 'flex',
      overflow: 'hidden',
      height: '100%',
      // kick the element into GPU rendering mode
      // this prevents background bleed problems when a parent is being translated
      // such as in the desktop carousel
      transform: 'translateZ(0px)',
      [theme.mediaQuery.tabletPortraitAndBelow]: {
        flex: [1, 0, 'calc(100% - 20px)'],
        height: 'auto',
      },
    },
    rounded: {
      borderRadius: [8, 8, 0, 0],
      flexGrow: 1,
      position: 'relative',
    },
    innerContainer: {
      padding: [15, 10],
      '&.isSearching': {
        padding: 0,
        height: '100%',
      },
      '& > *:not(:last-child)': {
        marginBottom: 15,
      },
    },
    doneButton: {
      display: 'flex',
      alignItems: 'center',
      color: theme.color.primary,
      transition: 'color 0.2s, opacity 0.2s',
      opactiy: 1,
      fontSize: 13,
      fontWeight: 600,
      '& >*:not(:last-child)': {
        marginRight: 2,
      },
      '&.disabled': {
        color: theme.color.basis4,
        opacity: 0.35,
      },
    },
    searchRow: {
      display: 'flex',
    },
    searchBar: {
      flexGrow: 1,
    },
  };
}

function mapStateToProps(state: GlobalState) {
  return {
    manageableFriends: manageFriendsSelector(state),
    anyFriends: some(state.friends.friends),
    allRequests: state.friendRequests.friendRequests,
    isSaving: state.friendGroups.savingGroups.has(NEW_GROUP_ID),
    groups: state.friendGroups.groups,
  };
}

const actions = {
  onCreateGroup: createGroup,
  onRemoveGroup: deleteGroup,
  onSearchUsers: searchUsers,
  onViewFriend: viewFriend,
  onClearSearchResults: clearResults,
};
export default connect(mapStateToProps, actions)(
  withRouter(useSheet(applyStyles)(GroupContentsNew)),
);
